import type maplibregl from 'maplibre-gl'
import { LayerTier, LAYER_TIER_MAP, getDefaultOrder } from '../../lib/layer-tier-registry.js'

export const ANCHOR_OVERLAYS = '__cairn_anchor_overlays'
export const ANCHOR_TIER1 = '__cairn_anchor_tier1'
export const ANCHOR_TIER2 = '__cairn_anchor_tier2'
export const ANCHOR_TIER3 = '__cairn_anchor_tier3'
export const ANCHOR_TIER4 = '__cairn_anchor_tier4'
export const ANCHOR_SHAPES = '__cairn_anchor_shapes'

const ALL_ANCHORS = [
  ANCHOR_OVERLAYS,
  ANCHOR_TIER1,
  ANCHOR_TIER2,
  ANCHOR_TIER3,
  ANCHOR_TIER4,
  ANCHOR_SHAPES,
]

const overlayGroups = new Map<string, string[]>()
const layerToGroup = new Map<string, string>()

export function ensureAnchors(map: maplibregl.Map): void {
  for (const id of ALL_ANCHORS) {
    if (!map.getLayer(id)) {
      map.addLayer({
        id,
        type: 'background',
        layout: { visibility: 'none' },
      })
    }
  }
}

function anchorAboveTier(tier: LayerTier): string {
  switch (tier) {
    case LayerTier.Imagery:
      return ANCHOR_TIER1
    case LayerTier.GroundFixed:
      return ANCHOR_TIER2
    case LayerTier.Data:
      return ANCHOR_TIER3
    case LayerTier.Atmospheric:
      return ANCHOR_TIER4
    case LayerTier.Reference:
      return ANCHOR_SHAPES
  }
}

export function addOverlayLayer(
  map: maplibregl.Map,
  spec: maplibregl.LayerSpecification,
  groupId?: string
): void {
  ensureAnchors(map)

  const tier = groupId ? LAYER_TIER_MAP.get(groupId)?.tier : undefined
  const beforeId = tier !== undefined ? anchorAboveTier(tier) : ANCHOR_TIER3

  map.addLayer(spec, beforeId)

  if (groupId) {
    const existing = overlayGroups.get(groupId) ?? []
    if (!existing.includes(spec.id)) {
      existing.push(spec.id)
      overlayGroups.set(groupId, existing)
    }
    layerToGroup.set(spec.id, groupId)
  }
}

export function untrackOverlayLayer(layerId: string): void {
  const groupId = layerToGroup.get(layerId)
  if (!groupId) return
  const layers = overlayGroups.get(groupId)
  if (layers) {
    const idx = layers.indexOf(layerId)
    if (idx >= 0) layers.splice(idx, 1)
    if (layers.length === 0) overlayGroups.delete(groupId)
  }
  layerToGroup.delete(layerId)
}

export function clearOverlayTracking(): void {
  overlayGroups.clear()
  layerToGroup.clear()
}

export function reorderAllOverlays(
  map: maplibregl.Map,
  userTierOverrides?: ReadonlyMap<string, number>
): void {
  ensureAnchors(map)

  const tierBuckets = new Map<LayerTier, string[]>()
  for (const tier of [
    LayerTier.Imagery,
    LayerTier.GroundFixed,
    LayerTier.Data,
    LayerTier.Atmospheric,
    LayerTier.Reference,
  ]) {
    tierBuckets.set(tier, [])
  }

  for (const groupId of overlayGroups.keys()) {
    const entry = LAYER_TIER_MAP.get(groupId)
    const tier = entry?.tier ?? LayerTier.Data
    tierBuckets.get(tier)!.push(groupId)
  }

  for (const [tier, groupIds] of tierBuckets) {
    if (groupIds.length === 0) continue

    const anchor = anchorAboveTier(tier)

    groupIds.sort((a, b) => {
      const orderA = userTierOverrides?.get(a) ?? getDefaultOrder(a)
      const orderB = userTierOverrides?.get(b) ?? getDefaultOrder(b)
      return orderA - orderB
    })

    for (const groupId of groupIds) {
      const layerIds = overlayGroups.get(groupId)
      if (!layerIds) continue
      for (const layerId of layerIds) {
        if (map.getLayer(layerId)) {
          map.moveLayer(layerId, anchor)
        }
      }
    }
  }
}

/** @deprecated Use reorderAllOverlays instead */
export function reorderOverlayGroups(map: maplibregl.Map, _topGroupIds: string[]): void {
  reorderAllOverlays(map)
}

export function addShapeLayer(map: maplibregl.Map, spec: maplibregl.LayerSpecification): void {
  ensureAnchors(map)
  map.addLayer(spec)
}
